/**
 * DeliveryReasonCodes — Delivery outcome reason codes.
 *
 * Most common codes listed first for quick selection.
 * Covers both success and failure scenarios for FCL & LCL.
 */

// ─── Success codes ───────────────────────────────────────────────────────────

export interface ReasonCode {
  code: string;
  label: string;
  /** Brief description shown on select */
  description?: string;
  /** Whether this code indicates a failed delivery */
  isFailure: boolean;
  /** Which transport types this applies to ('FCL' | 'LCL' | 'ALL') */
  scope: 'FCL' | 'LCL' | 'ALL';
}

/** Delivery success codes — most common first */
export const SUCCESS_CODES: ReasonCode[] = [
  { code: 'OK',              label: 'Delivered OK',              description: 'Normal delivery completed',               isFailure: false, scope: 'ALL' },
  { code: 'OK_PARTIAL',      label: 'Partial Delivery',          description: 'Some items delivered, remainder pending',  isFailure: false, scope: 'LCL' },
  { code: 'OK_SELF_COLLECT',  label: 'Self-Collected',           description: 'Customer self-collected cargo',            isFailure: false, scope: 'ALL' },
  { code: 'OK_DEVANNED',     label: 'Devanned & Delivered',      description: 'Container devanned, cargo delivered',      isFailure: false, scope: 'FCL' },
  { code: 'OK_RETURNED',     label: 'Empty Returned',            description: 'Empty container returned to depot',        isFailure: false, scope: 'FCL' },
  { code: 'OK_TRANSLOADED',  label: 'Transloaded',               description: 'Cargo transloaded to another vehicle',     isFailure: false, scope: 'ALL' },
];

/** Delivery failure codes — most common first */
export const FAILURE_CODES: ReasonCode[] = [
  { code: 'FAIL_NO_ONE',     label: 'No One Available',          description: 'No one at delivery location to receive',   isFailure: true, scope: 'ALL' },
  { code: 'FAIL_REFUSED',    label: 'Delivery Refused',          description: 'Consignee refused to accept cargo',        isFailure: true, scope: 'ALL' },
  { code: 'FAIL_WRONG_ADDR', label: 'Wrong Address',             description: 'Delivery address incorrect or not found',  isFailure: true, scope: 'ALL' },
  { code: 'FAIL_NO_ACCESS',  label: 'Cannot Access Site',        description: 'Vehicle cannot enter or reach location',   isFailure: true, scope: 'ALL' },
  { code: 'FAIL_DAMAGED',    label: 'Cargo Damaged',             description: 'Goods found damaged on arrival',           isFailure: true, scope: 'ALL' },
  { code: 'FAIL_SHORT',      label: 'Shortage / Missing Items',  description: 'Items missing vs. manifest',               isFailure: true, scope: 'LCL' },
  { code: 'FAIL_DOCS',       label: 'Document Issue',            description: 'Missing or wrong documents',               isFailure: true, scope: 'ALL' },
  { code: 'FAIL_SEAL',       label: 'Seal Broken / Tampered',    description: 'Container seal broken or tampered',         isFailure: true, scope: 'FCL' },
  { code: 'FAIL_OVERWEIGHT', label: 'Overweight',                description: 'Cargo exceeds weight limit at site',       isFailure: true, scope: 'ALL' },
  { code: 'FAIL_WEATHER',    label: 'Bad Weather',               description: 'Unable to deliver due to weather',         isFailure: true, scope: 'ALL' },
  { code: 'FAIL_BREAKDOWN',  label: 'Vehicle Breakdown',         description: 'Vehicle broke down en route',              isFailure: true, scope: 'ALL' },
  { code: 'FAIL_LATE',       label: 'Too Late / Closed',         description: 'Arrived after operating hours',            isFailure: true, scope: 'ALL' },
  { code: 'FAIL_OTHER',      label: 'Other',                     description: 'Specify reason in notes',                  isFailure: true, scope: 'ALL' },
];

/** All codes combined */
export const ALL_REASON_CODES = [...SUCCESS_CODES, ...FAILURE_CODES];

/** Get codes filtered by transport type */
export function getReasonCodes(tripType: string): { success: ReasonCode[]; failure: ReasonCode[] } {
  const isFCL = tripType.startsWith('FCL');
  const isLCL = tripType.startsWith('LCL');

  const filter = (code: ReasonCode) =>
    code.scope === 'ALL' ||
    (isFCL && code.scope === 'FCL') ||
    (isLCL && code.scope === 'LCL');

  return {
    success: SUCCESS_CODES.filter(filter),
    failure: FAILURE_CODES.filter(filter),
  };
}
